/**
 * The landing screen.
 *
 * It is deliberately thin, and it says so — but it must say the *current*
 * true thing. Until 2026-09-07 it still read "return preparation and
 * reconciliation are in development", written when that was the case and left
 * standing after the parity build shipped GSTR-1, GSTR-3B, reconciliation,
 * prepared-versus-filed, reports and download. The sidebar carried all six
 * while this paragraph said none of them existed.
 *
 * Stale reassurance is worse than no reassurance: a practitioner who believes
 * it does not go looking for the screens, and a stakeholder reading it
 * concludes nothing has been built. What is genuinely absent is the filing
 * step and a real dashboard, and that is what it now says.
 */

import { ArrowRight, Building2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { CLIENT, useEvaluation } from '../lib/access'
import { clients } from '../lib/api'

export function Overview() {
  const { can } = useEvaluation()
  const [count, setCount] = useState<number | null>(null)

  useEffect(() => {
    if (!can(CLIENT, 'can_view')) return
    clients
      .list({ limit: 1 })
      .then((body) => setCount(Number(body.meta?.total ?? 0)))
      .catch(() => setCount(null))
  }, [can])

  return (
    <div>
      <h1 className="mb-1 text-xl font-semibold text-foreground">ProGST</h1>
      <p className="mb-8 text-sm text-muted-foreground">
        GST compliance for your practice — returns and input-credit reconciliation.
      </p>

      {can(CLIENT, 'can_view') && (
        <Link
          to="/clients"
          className="gst-card flex max-w-sm items-center gap-4 p-5 transition-colors hover:border-primary/40"
        >
          <Building2 className="text-primary" size={22} />
          <span className="flex-1">
            <span className="block text-sm font-medium text-foreground">Clients</span>
            <span className="block text-xs text-muted-foreground">
              {count === null ? 'Taxpayers you file for' : `${count} on your list`}
            </span>
          </span>
          <ArrowRight size={16} className="text-muted-foreground" />
        </Link>
      )}

      {/* Still no dashboard tiles (filing calendar, returns due, 2B match
          rates). Left as an honest sentence rather than placeholder numbers:
          a fake number on a dashboard is believed, and the returns it would
          summarise are real now. See docs/DOMAIN_HANDOFF.md §6. */}
      <p className="mt-10 max-w-lg text-xs leading-relaxed text-muted-foreground">
        Preparation and reconciliation are here — GSTR-1, GSTR-3B, GSTR-2B matching and the
        prepared-versus-filed comparison, each reached from the sidebar.{' '}
        <strong className="font-medium text-foreground">Filing to the GST portal is not.</strong>{' '}
        Everything stops at a signed-off return and the files it produces, which you file
        yourself.
      </p>
    </div>
  )
}
